import { useState, useEffect, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useCalendarFeed } from '@/hooks/useCalendarFeed'
import { isWebcalSupported, toAppleCalendarUrl, toHttps, toWebcal } from '@/utils/feedUrl'

interface CalendarConnectModalProps {
  isOpen: boolean
  onClose: () => void
  feedUrl?: string | null
  isEnabled?: boolean
  qrUrl?: string | null
}

async function copyToClipboard(text: string): Promise<void> {
  // calendar-v2 (3.4): navigator.clipboard ist nur in secure contexts verfügbar
  // (https oder localhost). Im LAN-Betrieb (http://<IP>:5173) ist es undefined —
  // daher execCommand-Fallback.
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  try {
    if (!document.execCommand('copy')) {
      throw new Error('execCommand copy failed')
    }
  } finally {
    document.body.removeChild(textarea)
  }
}

function isValidFeedUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:' || parsed.protocol === 'webcal:') &&
           url.endsWith('.ics')
  } catch {
    return false
  }
}

export function CalendarConnectModal({ isOpen, onClose, feedUrl, isEnabled, qrUrl }: CalendarConnectModalProps) {
  const [copied, setCopied] = useState(false)
  // calendar-v3 (I4): Bestandsabo = Schritt 1 (erstellen) ist erledigt
  const [step, setStep] = useState(feedUrl ? 2 : 1)
  const { subscribe, resetToken, toggle, message, setMessage, isLoading, error } = useCalendarFeed()
  const [localFeedUrl, setLocalFeedUrl] = useState(feedUrl)
  const [localQrUrl, setLocalQrUrl] = useState(qrUrl ?? feedUrl)
  const [localEnabled, setLocalEnabled] = useState(isEnabled ?? false)
  const modalRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    if (feedUrl) setLocalFeedUrl(feedUrl)
    if (qrUrl) setLocalQrUrl(qrUrl)
    if (isEnabled !== undefined) setLocalEnabled(isEnabled)
  }, [feedUrl, qrUrl, isEnabled])
  
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])
  
  useEffect(() => {
    if (modalRef.current && isOpen) {
      modalRef.current.focus()
    }
  }, [isOpen])
  
  const handleCopy = async () => {
    if (!localFeedUrl) return
    if (!isValidFeedUrl(localFeedUrl)) {
      setMessage('Ungültige Feed-URL')
      return
    }
    try {
      await copyToClipboard(localFeedUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setMessage('Kopieren fehlgeschlagen — bitte URL manuell markieren')
    }
  }

  const handleAppleCalendar = async () => {
    if (!localFeedUrl || !isValidFeedUrl(localFeedUrl)) return
    // calendar-v6: Apple Kalender verlangt für Abos zwingend HTTPS — die
    // URL wird daher als webcal:// auf https-Basis übergeben (Apple upgraded
    // auf https; siehe utils/feedUrl.ts). webcals:// lehnt Safari bei
    // JS-Navigation mit «address is invalid» ab.
    // Nur Safari registriert den webcal-Handler zuverlässig; Chromium/
    // Firefox brechen sonst mit «The request for webcal://... failed» ab.
    // Ohne https-Seite (keine Zertifikate) würde auch das Abo scheitern —
    // dann lieber die URL kopieren und manuell abonnieren lassen.
    const appleUrl = toAppleCalendarUrl(localFeedUrl)
    if (!appleUrl) return
    if (isWebcalSupported() && window.location.protocol === 'https:') {
      window.location.href = appleUrl
      return
    }
    try {
      await copyToClipboard(toHttps(localFeedUrl))
      setMessage('Feed-URL wurde kopiert — in der Kalender-App: Ablage → Neues Kalenderabonnement → URL einfügen.')
    } catch {
      setMessage('URL manuell kopieren und in der Kalender-App unter Ablage → Neues Kalenderabonnement einfügen.')
    }
  }

  const handleGoogleCalendar = () => {
    if (!localFeedUrl || !isValidFeedUrl(localFeedUrl)) return
    const cid = encodeURIComponent(toWebcal(localFeedUrl))
    window.open(`https://calendar.google.com/calendar/render?cid=${cid}`, '_blank', 'noopener')
  }

  const handleOutlookCalendar = () => {
    if (!localFeedUrl || !isValidFeedUrl(localFeedUrl)) return
    const url = encodeURIComponent(localFeedUrl)
    window.open(
      `https://outlook.office.com/calendar/deeplink/compose?rru=addcalendar&url=${url}&path=/calendar/view`,
      '_blank',
      'noopener'
    )
  }
  
  const handleSubscribe = async () => {
    try {
      const result = await subscribe()
      setLocalFeedUrl(result.feed_url)
      setLocalQrUrl(result.qr_url ?? result.feed_url)
      setLocalEnabled(true)
      setStep(2)
    } catch {
      // Error handled by hook
    }
  }
  
  const handleReset = async () => {
    try {
      const result = await resetToken()
      setLocalFeedUrl(result.feed_url)
      setLocalQrUrl(result.qr_url ?? result.feed_url)
      setMessage('Token zurückgesetzt')
    } catch {
      // Error handled by hook
    }
  }
  
  const handleToggle = async () => {
    try {
      await toggle(!localEnabled)
      setLocalEnabled(!localEnabled)
    } catch {
      // Error handled by hook
    }
  }
  
  if (!isOpen) return null
  
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
            Kalender verbinden
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Schliessen"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {message && (
            <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              {message}
              <button onClick={() => setMessage(null)} className="ml-2 underline">Schliessen</button>
            </div>
          )}
          
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                1
              </div>
              <div className="text-sm font-medium text-gray-900">Feed erstellen</div>
            </div>
            
            {!localFeedUrl && (
              <button
                onClick={handleSubscribe}
                disabled={isLoading}
                className="w-full py-2 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isLoading ? 'Erstellen...' : 'Feed erstellen'}
              </button>
            )}
            
            {localFeedUrl && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    2
                  </div>
                  <div className="text-sm font-medium text-gray-900">URL kopieren &amp; Kalender verbinden</div>
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={localFeedUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`}
                  >
                    {copied ? 'Kopiert!' : 'Kopieren'}
                  </button>
                </div>

                <div className="space-y-2 mt-2">
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={handleGoogleCalendar}
                      disabled={!localFeedUrl || !isValidFeedUrl(localFeedUrl)}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Google
                    </button>
                    <button
                      onClick={handleAppleCalendar}
                      disabled={!localFeedUrl || !isValidFeedUrl(localFeedUrl)}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Apple
                    </button>
                    <button
                      onClick={handleOutlookCalendar}
                      disabled={!localFeedUrl || !isValidFeedUrl(localFeedUrl)}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Outlook
                    </button>
                  </div>

                  <p className="text-xs text-gray-400">
                    Apple öffnet die Kalender-App direkt. Google und Outlook
                    benötigen eine öffentlich erreichbare Adresse — alternativ
                    die URL oben kopieren und manuell einfügen.
                  </p>

                  <p className="text-xs text-gray-400 text-center mt-2">
                    oder QR-Code mit Smartphone scannen
                  </p>

                  <div className="flex flex-col items-center gap-1">
                    {localFeedUrl && isValidFeedUrl(localFeedUrl) ? (
                      <>
                        <div className="p-2 bg-white border border-gray-200 rounded-lg">
                          {/* calendar-v2 (3.3): QR enthält die webcal-Variante,
                              damit iOS/Android die Kalender-App öffnen statt eines
                              Browser-Downloads.
                              calendar-v5: Der QR nutzt die LAN-IP-Variante (qr_url),
                              damit das Smartphone im selben Netz den Dev-Server
                              erreicht — die Kopier-URL oben bleibt origin-basiert.
                              calendar-v6: QR bewusst über http (Spiegel-Port 5174) —
                              iOS akzeptiert http im LAN ohne Zertifikat; nur der
                              Mac-Button nutzt https (macOS verlangt TLS). */}
                          <QRCodeSVG
                            value={toWebcal(localQrUrl || localFeedUrl)}
                            size={96}
                            level="M"
                          />
                        </div>
                        {/* Fallback für Android: URL als Text zum Kopieren */}
                        <code className="text-[10px] text-gray-400 break-all max-w-[240px]">
                          {toWebcal(localQrUrl || localFeedUrl)}
                        </code>
                      </>
                    ) : (
                      <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-xs text-gray-400">QR</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {localFeedUrl && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Feed aktivieren</span>
                <button
                  onClick={handleToggle}
                  disabled={isLoading}
                  className={`relative w-11 h-6 rounded-full transition-colors ${localEnabled ? 'bg-primary-600' : 'bg-gray-200'}`}
                  aria-label={localEnabled ? 'Deaktivieren' : 'Aktivieren'}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${localEnabled ? 'translate-x-5' : ''}`}
                  />
                </button>
              </div>
              
              <button
                onClick={handleReset}
                disabled={isLoading}
                className="w-full py-2 border border-gray-200 rounded-lg font-medium text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {isLoading && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Token zurücksetzen
              </button>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded-lg font-medium text-sm hover:bg-gray-300 transition-colors"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
  )
}